import React, { useEffect, useState, useMemo } from 'react';
import { ethers } from 'ethers';
import chainData from '../../chains.js';
import { useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import { toast } from 'react-toastify';
import { sessionStorageGet } from '../../lib/window-shim';

function getStoredJSON(key) {
    return JSON.parse(sessionStorageGet(key) || "{}");
}

/**
 * CollectionActions component.
 *
 * Provides action buttons and status information for an NFT collection.
 * Features:
 * - Displays reward eligibility (if user has unclaimed rewards)
 * - Shows mint availability for mintable collections
 * - Navigation to item page
 * - Collection logo/backdrop upload for owners
 * - Owner earnings claim functionality
 *
 * Uses ethers.js to interact with smart contracts for rewards, minting,
 * and collection management. Shows toast notifications for claims and errors.
 *
 * @param {Object} props - Component props
 * @param {string|null} props.rInfo - Reward info (unused currently)
 * @param {Object} props.cData - Collection data with network, contractAddress, owner, id
 * @param {boolean} props.isMintable - Whether collection is still mintable
 * @param {Function} props.mintItem - Callback to mint a new item
 * @param {Array} props.item - Item data for navigation
 * @param {Function} props.history - Navigation function (TanStack Router)
 * @param {Object} props.wallet - Wallet info with accounts array
 * @param {Function} props.uploadCollImg - Callback to upload collection image
 * @returns {React.ReactElement} Action panel with buttons and status
 */
export const CollectionActions = ({
                                      rInfo,
                                      cData,
                                      isMintable,
                                      mintItem,
                                      item,
                                      history,
                                      wallet,
                                      uploadCollImg,
                                  }) => {
    const [internalRInfo, setInternalRInfo] = useState(null);
    const [rewardToast, setRewardToast] = useState(null);
    const [ownerToast, setOwnerToast] = useState(null);
    const [mintableToast, setMintableToast] = useState(null);
    const [internalIsMintable, setInternalIsMintable] = useState(isMintable);
    const { address } = useAppKitAccount();
    const { walletProvider } = useAppKitProvider('eip155');

    // Compute chain slug from cData.network (numeric index)
    const chainSlug = cData?.network !== undefined
        ? (chainData[cData.network]?.tokName?.toLowerCase() || 'sgb')
        : 'sgb';

    const isOwner = cData?.owner?.toLowerCase() === wallet?.accounts[0].address.toLowerCase() || getStoredJSON('token')?.account?.id === 1;

    const provider = useMemo(() => {
        if (walletProvider) {
            return new ethers.providers.Web3Provider(walletProvider);
        }
    }, [walletProvider]);

    useEffect(() => {
        const fetchRewardInfo = async () => {
            if (!cData?.contractAddress || !address || !provider) {
                return;
            }

            const abi = [
                'function COOLDOWN_TIME() view returns(uint256)',
                'function diLen() view returns(uint256)',
                'function getRewardsAmount(address) view returns (uint256 _amount)',
                'function getPayoutInfo() view returns (tuple(address addr, uint256 percentage, uint256 outstanding))',
                'function withdrawCut()',
                'function claimRewards(address)',
                'function claimOutstandingAmount(address)',
                'function totalSupply() view returns(uint256)',
                'function MAX_SUPPLY() view returns(uint256)',
                'function MINT_PRICE() view returns(uint256)',
                'function _MINT_PRICE() view returns(uint256)',
                'function cfg() view returns(tuple(uint256 MAX_SUPPLY,uint256 RewardsPoolPercent,uint256 secondarySalesRoyaltyPercent,' +
                'uint256 _MINT_PRICE,uint256 WLDiscount,address RMAddress,address StakingContractAddress,address WNat,address FTSORM,' +
                'address NFTDelegatorAddress,address BonusTokenAddress,uint256 Flags,bool isDisabled,address externalPriceAddress))'
            ];

            const contract = new ethers.Contract(cData.contractAddress, abi, provider);

            let isReownContract = false;
            try {
                await contract.COOLDOWN_TIME();
                isReownContract = true;
            } catch (e) {
                try {
                    await contract.diLen();
                    isReownContract = true;
                } catch (e) {
                }
            }

            if (isReownContract) {
                // Check for user rewards
                try {
                    const rewards = await contract.getRewardsAmount(address);
                    const rewardsFloat = parseFloat(ethers.utils.formatEther(rewards));

                    if (rewards && rewardsFloat > 0) {
                        setInternalRInfo(rewards);
                        const toastContent = () => (
                            <div style={{ cursor: "default" }}>
                                You are eligible for a reward of {rewardsFloat.toFixed(2)}{" "}{chainData[cData?.network]?.tokName}{" "}from this collection!<br />
                                <div
                                    style={{ textDecoration: "underline", cursor: "pointer" }}
                                    onClick={async () => {
                                        const signerProvider = new ethers.providers.Web3Provider(walletProvider);
                                        const signerContract = new ethers.Contract(cData.contractAddress, abi, signerProvider.getSigner());
                                        const options = { value: "0", gasLimit: "8000000" };
                                        try {
                                            if (cData.contractAddress === "0x862B713fEcEbC5304eD79A3a6AE8747Dd") {
                                                const rmc = new ethers.Contract("0x45f435B39c6B13ad7324249c96134dC2059d75EC", abi, signerProvider.getSigner());
                                                await rmc.claimOutstandingAmount(address, options);
                                            } else {
                                                await signerContract.claimRewards(address, options);
                                            }
                                            toast.success("Claim transaction sent!");
                                        } catch (e) {
                                            toast.error(<><b>Error claiming rewards:</b><br />{e.message.substring(0, 100)}</>);
                                        }
                                    }}>
                                    Click to claim
                                </div>
                            </div>
                        );

                        if (!rewardToast) {
                            setRewardToast(toast.success(toastContent, { toastId: "reward", autoClose: 30000 }));
                        } else {
                            toast.update("reward", { render: toastContent, autoClose: 30000 });
                        }
                    }
                } catch (e) {
                    console.error("Failed to get rewards amount:", e);
                }

                // Check for owner earnings if the current user is the owner
                if (isOwner) {
                    try {
                        const payoutInfo = await contract.getPayoutInfo();
                        const outstandingFloat = parseFloat(ethers.utils.formatEther(payoutInfo.outstanding));

                        if (outstandingFloat > 0) {
                            const toastContent = () => (
                                <div style={{ cursor: "pointer" }}>
                                    There are project owner earnings of {outstandingFloat.toFixed(2)}{" "}{chainData[cData?.network]?.tokName}{" "}from this collection still to be claimed.<br />
                                    <div style={{ textDecoration: "underline", cursor: "pointer" }}
                                        onClick={async () => {
                                            const signerProvider = new ethers.providers.Web3Provider(walletProvider);
                                            const signerContract = new ethers.Contract(cData.contractAddress, abi, signerProvider.getSigner());
                                            await signerContract.withdrawCut().catch(e => toast.error(<><b>Error claiming earnings:</b><br />{e.message.substring(0, 100)}</>));
                                        }}>
                                        <span style={{ textDecoration: "underline" }}>Click to claim</span>
                                    </div>
                                </div>
                            );
                            if (!ownerToast) {
                                setOwnerToast(toast.success(toastContent, { toastId: "owner", autoClose: 30000 }));
                            } else {
                                toast.update("owner", { render: toastContent, autoClose: 30000 });
                            }
                        }
                    } catch (e) {
                        console.error("Failed to get payout info:", e);
                    }
                }

                // Check if the collection is still mintable
                try {
                    let maxSupply, currentSupply, mintPrice;
                    const newConfig = await contract.cfg().catch(() => null);

                    if (newConfig?._MINT_PRICE) {
                        mintPrice = newConfig._MINT_PRICE;
                        maxSupply = newConfig.MAX_SUPPLY;
                    } else {
                        mintPrice = await contract.MINT_PRICE().catch(() => contract._MINT_PRICE().catch(() => null));
                        maxSupply = await contract.MAX_SUPPLY().catch(() => null);
                    }

                    currentSupply = await contract.totalSupply();

                    if (maxSupply && currentSupply && mintPrice) {
                        const remaining = parseInt(maxSupply.toString()) - parseInt(currentSupply.toString());

                        if (remaining > 0) {
                            setInternalIsMintable(true);
                            const priceFloat = parseFloat(ethers.utils.formatEther(mintPrice.toString()));

                            const toastContent = () => (
                                <div style={{ cursor: "default" }}>
                                    There are still {remaining} items in this collection available to mint!<br />
                                    <div
                                        style={{ cursor: "pointer" }}
                                        onClick={async () => await mintItem()}
                                    >
                                        <span style={{ textDecoration: "underline" }}>
                                            Mint one now!
                                        </span>
                                        ({priceFloat.toFixed(2)}{" "}{chainData[cData?.network]?.tokName})
                                    </div>
                                </div>
                            );

                            if (!mintableToast) {
                                setMintableToast(toast.info(toastContent, { toastId: "avail", autoClose: 30000 }));
                            } else {
                                toast.update("avail", { render: toastContent, autoClose: 30000 });
                            }
                        } else {
                            setInternalIsMintable(false);
                        }
                    }
                } catch (e) {
                    console.error("Failed to check if mintable:", e);
                }
            }
        };

        fetchRewardInfo();
    }, [cData, address, provider]);

    return (
        <div className="pixel-card" class="pixel-card actions-card">
            {internalRInfo ? (
                <>
                    You are eligible for a reward of &nbsp;
                    {parseFloat(ethers.utils.formatEther(internalRInfo.toString(10))).toFixed(2)}
                    &nbsp;{chainData[cData?.network].tokName}&nbsp;from this collection!
                    <br />
                </>
            ) : null}
            {internalIsMintable ? (
                <button className="pixel-btn mb-2 mt-1" onClick={async () => await mintItem()}>
                    Mint an item from this collection
                </button>
            ) : null}
            {item.length > 0 ? (
                <button className="pixel-btn mb-2 mt-1" onClick={() => history({ to: `/item/${chainSlug}/${item[0].coll}/${item[0].meta.edition || 1}` })}>
                    Go to Item Page
                </button>
            ) : (
                <button className="pixel-btn ms-1 mb-2 mt-1" onClick={() => history({ to: `/item/${chainSlug}/${cData.id}/1` })}>
                    Go to Item Page for item #1
                </button>
            )}

            {cData && (isOwner ? (
                <>
                    <label className="pixel-btn mb-2 mt-1" htmlFor="file1">Set Collection Logo</label>
                    <label className="pixel-btn mb-2 mt-1" htmlFor="file2">Set Collection Backdrop</label>
                    <input type="file" id="file1" style={{ display: 'none' }} onChange={(e) => uploadCollImg(e)} />
                    <input type="file" id="file2" style={{ display: 'none' }} onChange={(e) => uploadCollImg(e)} />
                </>
            ) : <p>Sign in with wallet to manage this collection if you own it.</p>)}
        </div>
    );
};